/**
 * Pure, framework-agnostic validation rules for Espace Pro representations.
 *
 * This module is the single source of truth for whether a representation is
 * submittable. It is consumed both by the per-step validation in
 * `lvc-create-event-add-representation` and by the final submission gate so a
 * form with errors can never be committed (PRO-48).
 *
 * It intentionally has no dependency on Minze/Locale/DOM so it can be unit
 * tested in isolation. Callers are responsible for turning the returned field
 * names / flags into localized error messages.
 */

import { todayLocalIso } from '../../../components/base/date/date-utils'

export type RepresentationDataType = 'event' | 'exhibition' | 'festival'

export interface RepresentationInput {
  startDate?: string
  endDate?: string
  startTime?: string
  endTime?: string
  presenceMode?: string
  reservationRequired?: boolean | string
  exhibitionType?: string
  freeShow?: boolean | string
  price?: string
  priceType?: string
  partOfFestival?: boolean | string
  festival?: string
  place?: string
  placeData?: unknown
  address?: string
  city?: string
  province?: string
  zipcode?: string
  virtualPlace?: string
  virtualPlaceData?: unknown
  virtualLocationUrl?: string
  ticketingUrl?: string
  /** Go-live / embargo date ("date de mise en ligne"); feeds datePublished + availabilityStart. */
  dateOfSale?: string
  languages?: unknown
  [key: string]: unknown
}

export interface RepresentationValidationResult {
  /** Required fields that are empty. */
  missingFields: string[]
  /** True when the end datetime precedes the start datetime. */
  endBeforeStart: boolean
  /** True when a ticketing URL is present but not a valid http(s) URL. */
  invalidTicketingUrl: boolean
  /** True when the go-live date is a later day than the representation start date. */
  publishDateAfterStart: boolean
  /** True when the start date is an earlier calendar day than today. */
  startInPast: boolean
}

const isTruthyFlag = (value: unknown): boolean => value === true || value === 'true'

/**
 * A ticketing URL is valid when it parses as an absolute http(s) URL with a
 * host. This matches the publication rule: a paid representation only shows on
 * the site when it carries a usable ticketing link (PRO-48).
 */
export function isValidTicketingUrl(url?: string | null): boolean {
  if (!url || typeof url !== 'string') return false
  const trimmed = url.trim()
  if (!trimmed) return false
  try {
    const parsed = new URL(trimmed)
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return false
    return Boolean(parsed.hostname)
  } catch {
    return false
  }
}

const toDateTime = (date?: string, time?: string): Date | null => {
  if (!date) return null
  const parsed = new Date(date)
  if (Number.isNaN(parsed.getTime())) return null
  if (time && typeof time === 'string' && time.includes(':')) {
    const [hoursStr, minutesStr] = time.split(':')
    const hours = parseInt(hoursStr, 10)
    const minutes = parseInt(minutesStr, 10)
    if (!Number.isNaN(hours) && !Number.isNaN(minutes)) {
      parsed.setHours(hours, minutes, 0, 0)
    }
  }
  return parsed
}

/**
 * Compares the full start/end datetime (date + optional time). Equal bounds are
 * allowed; only a strictly earlier end is invalid.
 */
export function isEndBeforeStart(
  startDate?: string,
  startTime?: string,
  endDate?: string,
  endTime?: string
): boolean {
  if (!startDate || !endDate) return false
  const start = toDateTime(startDate, startTime)
  const end = toDateTime(endDate, endTime)
  if (!start || !end) return false
  return end.getTime() < start.getTime()
}

/**
 * The go-live date ("date de mise en ligne") must not be a later calendar day
 * than the representation start date — an offer cannot be published after the
 * event it belongs to has already started. Compared on the date only.
 * This date feeds both datePublished and availabilityStart, so a single check
 * covers both.
 */
export function isPublishDateAfterStart(dateOfSale?: string, startDate?: string): boolean {
  if (!dateOfSale || !startDate) return false
  const publish = toDateTime(dateOfSale)
  const start = toDateTime(startDate)
  if (!publish || !start) return false
  return publish.getTime() > start.getTime()
}

export function isGoLiveDateFieldVisible(rep: RepresentationInput): boolean {
  return isTruthyFlag(rep?.reservationRequired) || !isTruthyFlag(rep?.freeShow)
}

/**
 * Mirrors the required-field logic historically embedded in the component,
 * plus the PRO-48 rule that a paid representation always requires a ticketing
 * URL (independently of the "enable ticketing" checkbox).
 */
/**
 * The representation start date cannot be an earlier calendar day than today —
 * partners create upcoming events, not past ones. Compared on the day only, in
 * local time, via lexicographic comparison of 'YYYY-MM-DD' strings (timezone
 * safe). `now` is injectable for deterministic testing.
 */
export function isStartInPast(startDate?: string, now: Date = new Date()): boolean {
  if (!startDate) return false
  return startDate.slice(0, 10) < todayLocalIso(now)
}

export function getRequiredFields(
  rep: RepresentationInput,
  dataType: RepresentationDataType
): string[] {
  let requiredFields: string[] = []

  if (dataType === 'exhibition') {
    requiredFields = [...requiredFields, 'exhibitionType']
    if (rep.exhibitionType !== 'permanent') {
      requiredFields = [...requiredFields, 'startDate', 'endDate']
    }
  } else if (dataType === 'event') {
    requiredFields = [
      ...requiredFields,
      'startTime',
      'endTime',
      'startDate',
      'endDate',
      'languages',
      'presenceMode',
    ]
  }

  const isPaid = !isTruthyFlag(rep.freeShow)
  if (isPaid) {
    requiredFields = [...requiredFields, 'price', 'priceType']
  }

  if (isTruthyFlag(rep.partOfFestival)) {
    requiredFields = [...requiredFields, 'festival']
  }

  if (rep.presenceMode === 'onsite' || rep.presenceMode === 'hybrid') {
    requiredFields = rep.placeData
      ? [...requiredFields, 'place']
      : [...requiredFields, 'place', 'address', 'city', 'province', 'zipcode']
  }
  if (rep.presenceMode === 'online' || rep.presenceMode === 'hybrid') {
    requiredFields = rep.virtualPlaceData
      ? [...requiredFields, 'virtualPlace']
      : [...requiredFields, 'virtualPlace', 'virtualLocationUrl']
  }

  // PRO-48: a paid representation must carry a ticketing URL, whether or not the
  // "enable ticketing" checkbox is ticked. The checkbox still forces it too.
  if (isPaid || isTruthyFlag(rep.reservationRequired)) {
    requiredFields = [...requiredFields, 'ticketingUrl']
  }

  return requiredFields
}

export function validateRepresentation(
  rep: RepresentationInput,
  dataType: RepresentationDataType,
  now: Date = new Date()
): RepresentationValidationResult {
  const requiredFields = getRequiredFields(rep, dataType)

  const missingFields = requiredFields.filter(field => !rep[field as keyof RepresentationInput])

  const datesRequired = requiredFields.includes('startDate') && requiredFields.includes('endDate')
  const endBeforeStart =
    datesRequired && Boolean(rep.startDate) && Boolean(rep.endDate)
      ? isEndBeforeStart(rep.startDate, rep.startTime, rep.endDate, rep.endTime)
      : false

  // A present-but-malformed ticketing URL is always invalid. An empty required
  // URL is already surfaced through missingFields.
  const invalidTicketingUrl = Boolean(rep.ticketingUrl) && !isValidTicketingUrl(rep.ticketingUrl)

  const publishDateAfterStart = isPublishDateAfterStart(rep.dateOfSale, rep.startDate)

  // Only gate on a past start date when the start date is actually required for
  // this representation type (e.g. permanent exhibitions carry no dates).
  const startInPast =
    datesRequired && Boolean(rep.startDate) ? isStartInPast(rep.startDate, now) : false

  return { missingFields, endBeforeStart, invalidTicketingUrl, publishDateAfterStart, startInPast }
}

export function isRepresentationValid(result: RepresentationValidationResult): boolean {
  return (
    result.missingFields.length === 0 &&
    !result.endBeforeStart &&
    !result.invalidTicketingUrl &&
    !result.publishDateAfterStart &&
    !result.startInPast
  )
}
